import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@predigle/micro/component-ui';
import { Router } from '@angular/router';
import { VesselService } from '../../services/vessel.service';
import { ToastService } from '../../services/toast.service';
import { ClarityService } from '../../services/clarity.service';
import { SailLoaderComponent } from '../shared/reusable/sail-loader/sail-loader.component';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-create-vessel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    SailLoaderComponent,
    MatIcon,
  ],
  templateUrl: './create-vessel.component.html',
  styleUrls: ['./create-vessel.component.scss'],
})
export class CreateVesselComponent {
  step: 'ownership' | 'details' = 'ownership';

  ownsVessel: boolean | null = null;
  isPrimaryVessel = true;

  hullColor: 'Light' | 'Dark' | 'Other' = 'Light';
  engineCount = 1;
  engines: any[] = [
    {
      engine_type: '',
      horse_power: '',
      fuel_type: 'Gasoline',
    },
  ];

  // Loader State
  showLoader = false;

  vessel = {
    name: '',
    homePort: '',
    make: '',
    model: '',
    length: '',
    draft: '',
    fuelCapacity: '',
    mmsi: '',
    commercialNumber: '',
  };
  // Submit loading flag for the final Next/Create button.
  isSubmitting = false;
  vesselPhotos: (File | null)[] = [null, null, null];
  vesselPhotosPreviews: (string | null)[] = [null, null, null];
  formSubmitted = false;
  constructor(
    private router: Router,
    private vesselService: VesselService,
    private clarityService: ClarityService,
    public toast: ToastService,
  ) {}

  setEngineCount(n: number) {
    this.engineCount = n;
    // Adjust engines array while preserving existing data
    if (this.engines.length < n) {
      for (let i = this.engines.length; i < n; i++) {
        this.engines.push({
          engine_type: '',
          horse_power: '',
          fuel_type: 'Gasoline',
        });
      }
    } else if (this.engines.length > n) {
      this.engines = this.engines.slice(0, n);
    }
  }

  continue() {
    if (this.ownsVessel) {
      this.step = 'details';
    } else if (this.ownsVessel === false) {
      this.router.navigate(['/home']);
    }
  }

  goBack() {
    if (this.step === 'details') {
      this.step = 'ownership';
    } else {
      // navigate to create profile page
      // replace with your actual route
      window.history.back();
    }
  }
  NextPage() {
    if (this.isSubmitting) return;
    this.formSubmitted = true;
    if (!this.vessel.name.trim()) {
      this.showError('Boat name is required');
      return;
    }
    this.isSubmitting = true;

    const payloadEngines = this.engines.map((e) => ({
      engine_type: e.engine_type || 'Unknown',
      horse_power: Number(e.horse_power) || 0,
      fuel_type: e.fuel_type === 'Gasoline' ? 'Gasoline' : 'Diesel',
    }));

    const details = {
      manufacturer: this.vessel.make,
      model: this.vessel.model,
      hull_color: this.hullColor.toLowerCase(),
      home_port: this.vessel.homePort,
      registration: {
        MMSI: this.vessel.mmsi || null,
        commercial_number: this.vessel.commercialNumber || null,
      },
    };

    const specifications = {
      length_overall: {
        value: Number(this.vessel.length) || 0,
        unit: 'meters',
      },
      draft: {
        value: Number(this.vessel.draft) || 0,
        unit: 'meters',
      },
      fuel_capacity: {
        value: Number(this.vessel.fuelCapacity) || 0,
        unit: 'liters',
      },
      engines: payloadEngines,
    };

    // Build FormData with binary file uploads
    const formData = new FormData();
    formData.append('name', this.vessel.name);
    formData.append('is_owned', String(this.ownsVessel === true));
    formData.append('is_primary', String(this.isPrimaryVessel));
    formData.append('details', JSON.stringify(details));
    formData.append('specifications', JSON.stringify(specifications));

    // Append each image as a binary file
    this.vesselPhotos.forEach((file) => {
      if (file) {
        formData.append('vessel_image', file, file.name);
      }
    });

    console.log('Create Vessel FormData built with', this.vesselPhotos.filter(f => f).length, 'image(s)');

    this.vesselService.createVessel(formData).subscribe({
      next: (res: any) => {
        this.showSuccess('Vessel created successfully');
        this.clarityService.setAction('action_create_vessel');
        
        // Show loader
        this.showLoader = true;

        setTimeout(() => {
          this.router.navigate(['/home']);
        }, 300);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        const message =
          err?.error?.message || err?.error?.error || 'Failed to create vessel';

        this.showError(message);
      },
    });
  }

  onPhotoSelected(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      // Find the first empty slot
      const emptyIndex = this.vesselPhotos.findIndex((p) => p === null);
      if (emptyIndex !== -1) {
        // Store the raw File object for binary upload
        this.vesselPhotos[emptyIndex] = file;
        // Generate a preview URL for the template
        this.vesselPhotosPreviews[emptyIndex] = URL.createObjectURL(file);
      }
    });

    // Reset the input so the same file can be selected again if needed
    (event.target as HTMLInputElement).value = '';
  }

  removePhoto(index: number) {
    // Revoke the object URL to free memory
    if (this.vesselPhotosPreviews[index]) {
      URL.revokeObjectURL(this.vesselPhotosPreviews[index]!);
    }
    this.vesselPhotos[index] = null;
    this.vesselPhotosPreviews[index] = null;
  }
  // ---------------- SNACKBAR HELPERS ----------------
  private showError(message: string) {
    this.toast.error(message);
  }

  private showSuccess(message: string) {
    this.toast.success(message);
  }
}
